import { supabase } from '@/lib/supabase';
import { SeoInternalLink } from '@/types/api/seo';

function mapLink(row: any): SeoInternalLink {
  return {
    id: row.id,
    projectId: row.project_id,
    auditId: row.audit_id,
    sourceUrl: row.source_url,
    targetUrl: row.target_url,
    anchorText: row.anchor_text,
    createdAt: row.created_at,
  };
}

const seoInternalLinksService = {
  async getLinkGraph(projectId: string): Promise<SeoInternalLink[]> {
    const { data, error } = await supabase
      .from('seo_internal_links')
      .select('*')
      .eq('project_id', projectId)
      .order('source_url', { ascending: true });

    if (error) throw error;
    return (data ?? []).map(mapLink);
  },

  async getLinkStats(projectId: string): Promise<{
    totalLinks: number;
    uniqueSourcePages: number;
    uniqueTargetPages: number;
    orphanPages: string[];
    topLinkedPages: Array<{ url: string; inboundCount: number }>;
  }> {
    const { data, error } = await supabase
      .from('seo_internal_links')
      .select('source_url, target_url')
      .eq('project_id', projectId);

    if (error) throw error;
    const links = data ?? [];

    const sourcePages = new Set<string>();
    const targetPages = new Set<string>();
    const inboundCounts = new Map<string, number>();

    for (const link of links) {
      sourcePages.add(link.source_url);
      targetPages.add(link.target_url);
      inboundCounts.set(link.target_url, (inboundCounts.get(link.target_url) ?? 0) + 1);
    }

    // All pages that appear as sources but never as targets = orphan candidates
    const allPages = new Set([...sourcePages, ...targetPages]);
    const orphanPages = [...allPages].filter((url) => !inboundCounts.has(url));

    // Top linked pages sorted by inbound count
    const topLinkedPages = [...inboundCounts.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 20)
      .map(([url, count]) => ({ url, inboundCount: count }));

    return {
      totalLinks: links.length,
      uniqueSourcePages: sourcePages.size,
      uniqueTargetPages: targetPages.size,
      orphanPages,
      topLinkedPages,
    };
  },

  async getInboundLinks(projectId: string, targetUrl: string): Promise<SeoInternalLink[]> {
    const { data, error } = await supabase
      .from('seo_internal_links')
      .select('*')
      .eq('project_id', projectId)
      .eq('target_url', targetUrl);

    if (error) throw error;
    return (data ?? []).map(mapLink);
  },

  async getOutboundLinks(projectId: string, sourceUrl: string): Promise<SeoInternalLink[]> {
    const { data, error } = await supabase
      .from('seo_internal_links')
      .select('*')
      .eq('project_id', projectId)
      .eq('source_url', sourceUrl);

    if (error) throw error;
    return (data ?? []).map(mapLink);
  },
};

export default seoInternalLinksService;
